import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { TuiAlertService } from '@taiga-ui/core';
import { TranslateService } from '@ngx-translate/core';
import { EXPENSES_DEFAULT_QUERY } from '../../constants/expenses.constants';
import { ExpensesDataSource } from '../../data/expenses.data-source';
import { Expense } from '../../models/expense.model';
import { ExpenseDetailSidebarComponent } from '../../components/expense-detail-sidebar/expense-detail-sidebar.component';
import { ExpenseFormLauncherService } from '../expense-form-launcher.service';

/**
 * Página corta: el desglose de precio es una tarjeta estrecha y no debe crecer
 * sin límite. Con menos de una página el paginador no se muestra.
 */
const PAGE_SIZE = 5;

/**
 * Gastos reportados por los conductores de un servicio, para el desglose de
 * precio del detalle.
 *
 * No hay endpoint «gastos de un servicio»: el listado paginado busca también por
 * número de orden, así que se filtra por `orderHash`. La paginación es la del
 * servidor; no se cargan todos los gastos de golpe.
 */
@Component({
  selector: 'app-expense-summary-list',
  templateUrl: './expense-summary-list.component.html',
  styleUrls: ['./expense-summary-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpenseSummaryListComponent implements OnChanges {
  /** Número de orden del servicio (`B-A0DA99CA`). */
  @Input() orderHash: string | null = null;

  @Input() currencySymbol: string | null = null;

  protected expenses: readonly Expense[] = [];
  protected totalCount = 0;
  protected page = 1;
  protected isLoading = false;
  protected hasError = false;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly dataSource: ExpensesDataSource,
    private readonly offcanvas: NgbOffcanvas,
    private readonly formLauncher: ExpenseFormLauncherService,
    private readonly alerts: TuiAlertService,
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(): void {
    this.page = 1;
    this.load();
  }

  /** `tui-pagination` trabaja con índice 0-based; la consulta es 1-based. */
  protected get pageIndex(): number {
    return Math.max(0, this.page - 1);
  }

  protected get pageCount(): number {
    return Math.ceil(this.totalCount / PAGE_SIZE);
  }

  protected get resultsKey(): string {
    return this.totalCount === 1 ? 'expenses.results_one' : 'expenses.results';
  }

  protected onPageIndexChange(index: number): void {
    this.page = index + 1;
    this.load();
  }

  protected openDetail(expense: Expense): void {
    const offcanvasRef = this.offcanvas.open(ExpenseDetailSidebarComponent, {
      position: 'end',
      panelClass: 'expenses-offcanvas',
    });

    offcanvasRef.componentInstance.expense = expense;

    // Aquí ya estamos en el detalle del servicio: el enlace sería un bucle.
    offcanvasRef.componentInstance.showServiceLink = false;

    /*
     * El panel de detalle no abre el formulario: se cierra devolviendo `'edit'`
     * y la transición se hace aquí, para no apilar dos paneles laterales.
     */
    offcanvasRef.result
      .then((result) => {
        if (result === 'edit') {
          this.openEdit(expense);
        }
      })
      // Cierre por backdrop o Escape: no hay nada que hacer.
      .catch(() => undefined);
  }

  /**
   * Edita el gasto desde el desglose de precio.
   *
   * Al guardar se recarga la página actual en lugar de parchear la tarjeta: el
   * importe editado cambia el desglose, y el gasto puede dejar de cumplir el
   * filtro por número de orden si se movió de servicio.
   */
  private openEdit(expense: Expense): void {
    this.formLauncher
      .open('edit', expense)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.alerts
          .open(this.translate.instant('expenses.alerts.updated'), {
            status: 'success',
          })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe();

        this.load();
      });
  }

  protected trackById(_index: number, expense: Expense): string {
    return `${expense.nodeId}|${expense.expenseId ?? ''}`;
  }

  private load(): void {
    const orderHash = this.orderHash?.trim();

    if (!orderHash) {
      this.expenses = [];
      this.totalCount = 0;
      return;
    }

    this.isLoading = true;
    this.hasError = false;
    this.cdr.markForCheck();

    this.dataSource
      .list({
        ...EXPENSES_DEFAULT_QUERY,
        searchTerm: orderHash,
        page: this.page,
        pageSize: PAGE_SIZE,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          /*
           * La búsqueda del backend es difusa: compara el término contra número
           * de orden, matrícula y nombre del conductor. Se queda sólo con los del
           * pedido para no mostrar gastos ajenos en este desglose. Con un hash de
           * pedido la coincidencia es exacta en la práctica, así que el total del
           * servidor sirve para el paginador.
           */
          this.expenses = result.items.filter(
            (expense) => expense.orderHash === orderHash
          );
          this.totalCount = result.totalCount;
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.expenses = [];
          this.totalCount = 0;
          this.isLoading = false;
          this.hasError = true;
          this.cdr.markForCheck();
        },
      });
  }
}
